import type {
  PlatformEventCell,
  PlatformEventGroup,
  PlatformEventsPivot,
} from '../models/platformEvent.types'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function numeric(value: unknown): number {
  const result = Number(value)
  return Number.isFinite(result) ? result : 0
}

function eventNameOf(row: Readonly<Record<string, unknown>>): string {
  return typeof row.event_name === 'string' ? row.event_name : ''
}

function createEventGroup(row: Readonly<Record<string, unknown>>): PlatformEventGroup {
  return {
    marketingChannel: typeof row.marketing_channel === 'string' ? row.marketing_channel : null,
    values: {},
    total: 0,
  }
}

function eventCellOf(breakdown: unknown, eventName: string): PlatformEventCell | null {
  if (!isRecord(breakdown) || typeof breakdown.date !== 'string' || !breakdown.date) return null
  return {
    ...breakdown,
    date: breakdown.date,
    event_name: eventName,
    event_count: numeric(breakdown.event_count),
  }
}

/** Pure event-by-date pivot for the future module-08 MatrixTable adapter. */
export function pivotEventsByDate(rows: readonly Record<string, unknown>[]): PlatformEventsPivot {
  const byEvent: Record<string, PlatformEventGroup> = {}
  const events: string[] = []
  const dateSet = new Set<string>()

  for (const row of rows) {
    const eventName = eventNameOf(row)
    if (!eventName) continue
    let eventGroup = byEvent[eventName]
    if (!eventGroup) {
      events.push(eventName)
      eventGroup = createEventGroup(row)
      byEvent[eventName] = eventGroup
    }
    if (typeof row.marketing_channel === 'string') {
      eventGroup.marketingChannel = row.marketing_channel
    }

    const breakdowns = Array.isArray(row.breakdown_values) ? row.breakdown_values : []
    for (const breakdown of breakdowns) {
      const cell = eventCellOf(breakdown, eventName)
      if (!cell) continue
      eventGroup.values[cell.date] = cell
      dateSet.add(cell.date)
    }
  }

  const dates = [...dateSet].sort((left, right) => Date.parse(right) - Date.parse(left))
  const totalsByDate = Object.fromEntries(dates.map((date) => [date, 0]))
  for (const group of Object.values(byEvent)) {
    group.total = Object.values(group.values).reduce((sum, cell) => sum + cell.event_count, 0)
    for (const [date, cell] of Object.entries(group.values)) {
      totalsByDate[date] = (totalsByDate[date] ?? 0) + cell.event_count
    }
  }

  return { byEvent, events, dates, totalsByDate }
}
